import createClient from 'openapi-fetch';
import { authorizedFetch, currentUserId, withAuthHeaders } from '@/infrastructure/clients/BackendClient';
import { BACKEND_URL } from '@/infrastructure/clients/backendEnv';
import { fetchWithResilience } from '@/infrastructure/clients/resilientFetch';
import { offlineSaves } from './offlineSaves';
import { ApiProblem } from './problem';
import { apiPath, regionRouting, shareCacheVersion, type RegionRouting } from './routing';
import { sharedReads } from './sharedReads';
import { getDeployEnvironment } from '@/lib/deployEnvironment';
import type { paths } from '@/generated/contracts/api-v1';

/**
 * The app's one way to the backend. Every request to it goes through `sendToBackend`, which sends what must be answered
 * in Frankfurt there (routing.ts): the typed client `v1` below, the session calls (BackendClient's v1App), the change
 * streams (`openChangeStream`) and what is sent as the page goes away (`sendOnLeave`).
 */

type Transport = (input: string | Request, init: RequestInit) => Promise<Response>;

const routing = regionRouting();

// The highest version of the cache, and the window that sends reads to Frankfurt, are kept for the user's other tabs of
// the app on the same backend, and for this one once it reloads, and taken now, before anything is sent (routing.ts's
// shareCacheVersion). A page with no window (a test's) keeps its own.
if (typeof window !== 'undefined') shareCacheVersion(routing, `lureo-cache-version ${BACKEND_URL}`, window);

const methodOf = (input: string | Request, init: RequestInit) => (init.method ?? (typeof input === 'string' ? 'GET' : input.method)).toUpperCase();

/**
 * Sends one request to the backend, with `fly-force-region` and `X-Lureo-Asked-Home` when it must be answered in
 * Frankfurt and `X-Lureo-Read-After` on a read that may be answered nearby (routing.ts). Every request is noted as
 * answered once, whatever becomes of it: with its answer's headers, where a write's version is read, or with none when
 * it failed or was aborted, so the reads after a write with no version go to Frankfurt for a while.
 */
export function sendToBackend(input: string | Request, init: RequestInit = {}, send: Transport = (request, requestInit) => fetch(request, requestInit)): Promise<Response> {
  const method = methodOf(input, init);
  const url = typeof input === 'string' ? input : input.url;
  const headers = routing.route(method, url, new Headers(init.headers ?? (typeof input === 'string' ? undefined : input.headers)));
  let sending: Promise<Response>;
  try {
    sending = send(input, { ...init, headers });
  } catch (error) {
    routing.answered(method, url);
    return Promise.reject(error);
  }
  return sending.then(response => {
    routing.answered(method, url, response.headers);
    return response;
  }, error => {
    routing.answered(method, url);
    throw error;
  });
}

/**
 * The typed /api/v1 client (contracts/api-v1.json). Every call carries the app key and the
 * session's bearer and refreshes and retries once on an auth failure (BackendClient.authorizedFetch).
 * When set-session refuses the refresh token a write signs out to login, while a GET drops the session
 * and is retried once with the app key alone, as the generic route read public content. A GET has the
 * generic route's timeout and read retries (fetchWithResilience); a write has no client abort, so a
 * long copy or delete is never cut off while the server finishes it. The editor's saves, which are safe to send twice,
 * are the exception: offlineSaves.ts gives each send of one a time limit, and sends it again after it.
 *
 *   const { data, error } = await toResult(v1.GET('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: id } } }));
 */
const authorized = (request: Request) => authorizedFetch(request, {}, {
  send: (input, init) => sendToBackend(input, init, (routed, routedInit) => fetchWithResilience(routed, routedInit, request.method === 'GET' ? undefined : null)),
  anonymousReadFallback: true,
});

/** Reads on their way are shared, and a screen's one read answers the reads it stands for as the screen opens (sharedReads.ts). */
const reads = sharedReads(authorized, undefined, { userId: () => currentUserId(), environment: () => getDeployEnvironment() });
const send = (request: Request) => reads.fetch(request);

/** The open scene's saves, answered on the device and sent in the background, online and offline (offlineSaves.ts). */
export const offline = offlineSaves({ userId: () => currentUserId() });

/** The editor is opening this scene: the reads its opening makes are answered from one read of its workspace (getEditorWorkspace). */
export const sceneOpening = (sceneId: string) => reads.opened(sceneId);

/**
 * The app is starting for whoever is signed in, now or once they sign in: the user, membership, org, environment,
 * remote config and version it reads first are answered from one read of the session bootstrap (getSessionBootstrap).
 */
export const sessionOpening = () => reads.sessionOpened();

// Each write through the typed client that could change what a scene shows, and each change from elsewhere (editCount)
let edits = 0;

/** Writes that change nothing a scene shows: storage (uploads and their files), the client's sighting and sessions, guidance, lessons, notifications, feedback. */
const NOT_AN_EDIT = /^\/api\/v1\/(uploads|storage-objects|client-sightings|sessions|guidance-[^/]+|lesson-runs|notifications|user-feedback|bug-reports)(\/|$)/;

function countEdit(request: Request): void {
  if (request.method === 'GET' || request.method === 'HEAD') return;
  if (!NOT_AN_EDIT.test(apiPath(request.url) ?? '')) edits += 1;
}

/**
 * A number that goes up with each write made through the typed client that could change what a scene shows (not a file
 * stored or deleted), and each change from elsewhere: when it hasn't moved, nothing the scene shows has changed.
 */
export const editCount = () => edits;

/**
 * A change from elsewhere, from either change stream, with the version of the cache its event names (`cacheSeq`): what
 * screens read as they opened no longer answers their reads, and the reads it sets off carry that version, so a region
 * answers them only from a copy of the cache that has the change. One that names none (or changes a reconnected stream
 * missed) sends them to Frankfurt for a while, since a region's cache may still hold the old answer.
 */
function changedElsewhere(cacheSeq?: number | null): void {
  edits += 1;
  routing.changedElsewhere(cacheSeq);
  reads.forgetContent();
}

/** A change to the open scene from elsewhere: what the editor's opening read no longer answers its reads. */
export const sceneChangedElsewhere = (cacheSeq?: number | null) => changedElsewhere(cacheSeq);

/** A change to the account from elsewhere (another tab, Claude): what screens read as they opened no longer answers their reads. */
export const accountChangedElsewhere = (cacheSeq?: number | null) => changedElsewhere(cacheSeq);

/**
 * This tab's own change, echoed on a change stream, with the version of the cache the stream's drop of it took: the
 * reads after it carry that version too, in case its write's answer named none. Nothing on the page changed that it
 * hasn't shown, so no screen is told and, with no version, no read is sent to Frankfurt.
 */
export const ownChangeEchoed = (cacheSeq?: number | null) => routing.raiseReadAfter(cacheSeq);

/**
 * The highest version of the cache this tab has seen, which its reads that may be answered nearby carry, and each rise
 * of it; and the window that sends them to Frankfurt instead when there is no version to wait for, and each time it
 * opens (routing.ts). The renderer sends no writes, so it learns both from here (rendererBridge's followReadAfter).
 */
export const cacheVersion: Pick<RegionRouting, 'readAfter' | 'onReadAfterRise' | 'readHomeUntil' | 'onReadHome'> = {
  readAfter: () => routing.readAfter(),
  onReadAfterRise: listener => routing.onReadAfterRise(listener),
  readHomeUntil: () => routing.readHomeUntil(),
  onReadHome: listener => routing.onReadHome(listener),
};

/**
 * Someone signed in or out. When the user is not the one before, nothing read for that one answers a read again, and
 * the saves they made that are still waiting are dropped rather than sent with the new session's bearer.
 */
export function sessionChanged(): void {
  reads.sessionChanged();
  offline.sessionChanged();
}

/** A number that goes up with each write that could change a read and each change from elsewhere, so a screen can tell its one read is still what the server would answer. */
export const readEpoch = () => reads.epoch();

/** The version (getVersion), which is the same for every caller, as the session bootstrap answered it while the app starts; null otherwise. For v1App, which sends the app key alone. */
export function versionAtStart(request: Request): Promise<Response | null> {
  return request.method === 'GET' && apiPath(request.url) === '/api/v1/version' ? reads.answer(request) : Promise.resolve(null);
}

export const v1 = createClient<paths>({
  baseUrl: BACKEND_URL,
  fetch: request => {
    countEdit(request);
    return offline.fetch(request, send);
  },
});

/**
 * Opens one of v1's change streams (streamSceneChanges, streamMyChanges) at `path`. Read with fetch, since EventSource
 * cannot send the bearer; an auth failure refreshes and retries once, and one that lasts signs out, as a write does.
 */
export function openChangeStream(path: string, signal: AbortSignal): Promise<Response> {
  return authorizedFetch(`${BACKEND_URL}${path}`, { headers: { Accept: 'text/event-stream' }, signal }, {
    send: (input, init) => sendToBackend(input, init),
  });
}

/**
 * A write sent as the page goes away: keepalive, so the request outlives the page, sent at once with the stored
 * session's bearer (there is no page left to wait for a refresh). It fails quietly.
 */
export function sendOnLeave(method: 'POST', path: keyof paths, body: unknown): void {
  try {
    void sendToBackend(`${BACKEND_URL}${path}`, { method, keepalive: true, headers: withAuthHeaders(), body: JSON.stringify(body) }).catch(() => undefined);
  } catch {
    // Nothing is left to tell
  }
}

const STORAGE_OBJECT = '/api/v1/storage-objects/{bucket}/{path}' satisfies keyof paths;

/**
 * The credential-free address of a public storage object (getStorageObject). Each path segment is encoded on
 * its own, so the address of a folder takes relative paths: a loader resolves `Brush/main.glsl` against it.
 */
export function storageObjectUrl(bucket: string, path: string): string {
  const segments = path.split('/').map(encodeURIComponent).join('/');
  return `${BACKEND_URL}${STORAGE_OBJECT.replace('{bucket}', encodeURIComponent(bucket)).replace('{path}', segments)}`;
}

/**
 * The bodySerializer of a multipart operation: one part per field, a Blob as a file part, nulls left out.
 *
 *   v1.PUT('/api/v1/storage-objects', { body: { file: binary(blob), bucket, path }, bodySerializer: multipart })
 */
export function multipart(body: Record<string, unknown>): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(body)) {
    if (value == null) continue;
    if (value instanceof Blob) form.append(name, value, value instanceof File ? value.name : name);
    else form.append(name, typeof value === 'string' ? value : JSON.stringify(value));
  }
  return form;
}

/** A Blob as the contract types a binary part (format: binary is a string to openapi-typescript). */
export const binary = (blob: Blob) => blob as unknown as string;

/** The outcome of a call: the data, or the problem that stopped it. Never both. */
export type V1Result<T> = { data: T; error: null } | { data: null; error: ApiProblem };

/** What an openapi-fetch call resolves to, loosely: enough to read either side. */
export interface Fetched<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Settles a v1 call into `{ data, error }` and never throws. Any non-2xx status is an ApiProblem,
 * as is a call that failed before a response arrived. A 204 resolves with `data: null`.
 */
export async function toResult<T>(pending: Promise<Fetched<T>>): Promise<V1Result<T>> {
  try {
    const { data, error, response } = await pending;
    if (!response.ok) return { data: null, error: ApiProblem.fromResponse(response, error) };
    return { data: (data ?? null) as T, error: null };
  } catch (error) {
    return { data: null, error: ApiProblem.fromError(error) };
  }
}
